import { css } from '@emotion/css';
import cls from 'classnames';
import { useState } from 'react';
import SwiperCore from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import theme from 'tailwind/theme';

import TicketCard from '@/components/account/ticket-card';
import CarouselNavButton from '@/components/carousel-nav-button';
import DownloadIcon from '@/components/icons/download-icon';
import Modal from '@/components/modal';
import useModal from '@/components/modal/use-modal';
import { PrizeItems } from '@/types/apis';

const swiperClass = css`
  .swiper-slide {
    display: flex;
    justify-content: center;
    transition: all 200ms linear;

    &.swiper-slide-active {
      transform: scale(1);
    }

    &.swiper-slide-prev {
      transform: translateX(10%) scale(0.8);
    }

    &.swiper-slide-next {
      transform: translateX(-10%) scale(0.8);
    }
  }

  @media (min-width: 768px) {
    .swiper-slide-prev {
      transform: scale(0.9);
    }

    .swiper-slide-next {
      transform: scale(0.9);
    }
  }
`;

const INITIAL_TICKET_POSITION = 1;

interface TicketModalProps {
  image?: string;
  title: string;
  tickets: PrizeItems[];
  modus?: string;
  amount: number;
  isOpen: boolean;
  closeModal: () => void;
}

const TicketModal = ({
  image,
  title,
  tickets,
  modus,
  isOpen,
  closeModal,
  amount,
}: TicketModalProps) => {
  const {
    isModalOpen: isDownloadInfoOpen,
    setIsModalOpen: setIsDownloadInfoOpen,
  } = useModal(false);
  const [mainSwiper, setMainSwiper] = useState<SwiperCore | null>(null);
  const [activeTicketPosition, setActiveTicketPosition] = useState<number>(
    INITIAL_TICKET_POSITION,
  );

  const updateTicketPosition = () => {
    if (mainSwiper) {
      setActiveTicketPosition(mainSwiper.activeIndex + 1);
    }
  };

  const openDownloadInfo = () => setIsDownloadInfoOpen(true);
  const closeDownloadInfo = () => setIsDownloadInfoOpen(false);

  return (
    <>
      {isOpen && (
        <Modal
          onClose={closeModal}
          headerActions={
            <div
              role="button"
              aria-label="Download-Informationen"
              className="w-8 h-8 bg-white flex justify-center items-center rounded-16 cursor-pointer"
              onClick={openDownloadInfo}
            >
              <DownloadIcon fill={theme.colors.black.DEFAULT} />
            </div>
          }
        >
          <div className="w-full h-full flex flex-col items-center justify-between pt-16 pb-4">
            <div className="w-full h-full md:max-w-[480px] md:mx-auto">
              <div className="flex flex-col justify-center h-full">
                <Swiper
                  wrapperTag="div"
                  slidesPerView={1.35}
                  centeredSlides
                  observer
                  onSwiper={setMainSwiper}
                  className={swiperClass}
                  style={{ margin: 0, width: '100%' }}
                  breakpoints={{
                    768: {
                      slidesPerView: 1,
                    },
                  }}
                  onSlideNextTransitionStart={updateTicketPosition}
                  onSlidePrevTransitionStart={updateTicketPosition}
                >
                  {tickets.map((ticket) => (
                    <SwiperSlide tag="div" key={ticket.id}>
                      <TicketCard
                        image={image}
                        title={title}
                        barcode={ticket.barcode}
                        url={ticket.url}
                        modus={modus}
                        status={ticket.wasScanned}
                        ticketType={ticket.name}
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
                <div className="flex justify-center items-center gap-8 mt-8">
                  <div onClick={() => mainSwiper?.slidePrev()}>
                    <CarouselNavButton
                      type="prev"
                      smSize="small"
                      mdSize="medium"
                      className={cls(
                        mainSwiper?.isBeginning &&
                          'text-gray-1 bg-gray-2 cursor-none',
                      )}
                    />
                  </div>

                  <div>
                    <span>{`${activeTicketPosition}/${amount}`}</span>
                  </div>

                  <div onClick={() => mainSwiper?.slideNext()}>
                    <CarouselNavButton
                      type="next"
                      smSize="small"
                      mdSize="medium"
                      className={cls(
                        mainSwiper?.isEnd &&
                          'text-gray-1 bg-gray-2 cursor-none',
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {isDownloadInfoOpen && (
        <Modal onClose={closeDownloadInfo}>
          <div className="w-full h-full flex flex-col justify-between px-5 pt-20 pb-8">
            <ul className="overflow-auto list-disc pl-5 space-y-4 text-body1">
              <li>
                Du kannst dieses Mobile Ticket beim Eingang vorweisen. Es muss
                nicht zusätzlich gespeichert oder ausgedruckt werden.
              </li>
              <li>
                Gerne darfst du einen Screenshot machen, um das Ticket offline
                verfügbar zu haben.
              </li>
            </ul>
          </div>
        </Modal>
      )}
    </>
  );
};

export default TicketModal;
